<template>
  <div class="why-buy-from-article" :class="!withDivider && 'has-divider'">
    <Divider margin-top="var(--space-10)" margin-bottom="var(--space-10)" v-if="withDivider" />

    <h2 class="why-buy-heading light new-pdp-title">Why buy from Article?</h2>

    <div class="content desktop">
      <div class="block" v-for="(reason, index) in reasons" :key="index">
        <div class="icon">
          <component :is="reason.icon"></component>
        </div>
        <div class="reason-title-wrapper">
          <h4 class="reason-title">{{ reason.title }}</h4>
        </div>
        <p class="reason-description">{{ reason.description }}</p>
        <a class="reason-link secondary accent" :href="reason.link.url" @click="onLinkClick(reason)">{{ reason.link.title }}</a>
      </div>
    </div>

    <div ref="mobileContent" class="content mobile">
      <div v-for="(reason, index) in reasons" :key="index" class="mobile-content-wrapper" :ref="'mobileContentWrapper' + index">
        <div class="mobile-content" @click.passive="onAccordionClick(index)">
          <div class="icon">
            <component :is="reason.icon"></component>
          </div>
          <h4 class="reason-title">
            <span class="reason-text">{{ reason.title }}</span>
            <span class="accordion-icons af" :class="[isItemSelected(index) ? 'af-minus' : 'af-plus']"></span>
          </h4>
        </div>
        <div class="accordion-content" :class="[isItemSelected(index) && 'active']">
          <p class="reason-description">{{ reason.description }}</p>
          <a class="reason-link secondary accent" :href="reason.link.url">{{ reason.link.title }}</a>
        </div>
        <Divider v-if="index < reasons.length - 1" height="2px" margin-top="var(--space-6)" margin-bottom="var(--space-6)" />
      </div>
    </div>
  </div>
</template>

<script>
import greatValueIcon from '@/assets/product/great-value.svg';
import fastDeliveryIcon from '@/assets/product/fast-delivery.svg';
import guaranteeIcon from '@/assets/product/30-day-guarantee.svg';
import speakLanguageIcon from '@/assets/product/speak-your-language.svg';
import { Divider } from '@/pixel/index';

export default {
  name: 'WhyBuyFromArticle',
  components: {
    Divider,
    greatValueIcon,
    guaranteeIcon,
    speakLanguageIcon,
    fastDeliveryIcon,
  },
  data() {
    return {
      selected: [],
      reasons: [
        {
          icon: greatValueIcon,
          title: 'Modern Furniture at Fair Prices',
          description: 'Our promise? High-quality furniture at radically lower (and much fairer) prices than comparable retailers.',
          link: {
            title: 'Learn more',
            url: '/value',
          },
        },
        {
          icon: fastDeliveryIcon,
          title: 'Free Shipping',
          description: 'Orders of $999 or more qualify for free basic shipping within our standard service area.',
          link: {
            title: 'Learn more',
            url: '/delivery',
          },
        },
        {
          icon: guaranteeIcon,
          title: '30-Day Satisfaction Guarantee',
          description: 'We’re confident you’ll love your new Article furniture, but just to make sure, you have 30 days to try it out.',
          link: {
            title: 'Learn more',
            url: '/returns',
          },
        },
        {
          icon: speakLanguageIcon,
          title: 'We’re Here To Help',
          description: 'If questions arise, our friendly and knowledgeable Customer Care team is just a phone call, chat, or email away.',
          link: {
            title: 'Contact us',
            url: '/contact',
          },
        },
      ],
    };
  },
  props: {
    withDivider: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    onAccordionClick(itemIndex) {
      const index = this.selected.indexOf(itemIndex);
      if (index > -1) {
        this.selected.splice(index, 1);
      } else {
        this.selected.push(itemIndex);
      }
      this.$emit('interaction', 'accordion');
    },

    onLinkClick(reason) {
      this.$emit('interaction', reason);
    },

    isItemSelected(itemIndex) {
      return this.selected.includes(itemIndex);
    },
  },
};
</script>

<style lang="postcss" scoped>
@import '@/pixel/style/variables';

.why-buy-from-article {
  padding-bottom: var(--space-10);

  .why-buy-heading {
    margin-bottom: var(--space-3);

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      margin-bottom: var(--space-6);
    }
  }

  .icon {
    width: 40px;
    height: 40px;
  }

  .reason-title {
    letter-spacing: normal;
    text-transform: none;
    font-size: var(--font-size-20);
    line-height: var(--line-height-24);
    font-family: var(--font-regular);
    color: var(--grey-medium);
  }

  .content {
    &.desktop {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: var(--space-10);

      .block {
        .reason-title-wrapper {
          padding-top: var(--space-4);
          padding-bottom: var(--space-2);
        }

        .reason-description {
          line-height: 24px;
          font-size: var(--font-size-16);
        }
      }

      @media (max-width: calc($extra-large-breakpoint - 1px)) {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(2, 1fr);
        row-gap: var(--space-0);
      }

      @media (min-width: $desktop-breakpoint) and (max-width: calc($extra-large-breakpoint - 1px)) {
        column-gap: var(--space-10);
      }

      @media (max-width: calc($mobile-breakpoint - 1px)) {
        display: none;
      }
    }

    &.mobile {
      @media (min-width: $mobile-breakpoint) {
        display: none;
      }

      .mobile-content-wrapper {
        .mobile-content {
          display: flex;
          align-items: center;

          .reason-title {
            color: var(--grey-medium);
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;

            .reason-text {
              padding-right: var(--space-3);
              flex-basis: 90%;
            }

            .accordion-icons {
              background-color: transparent;
              width: 24px;
              height: 24px;
              text-align: center;
              border-radius: 12px;
              flex-basis: 10%;
            }
          }
        }

        .accordion-content {
          transition: max-height ease-out 250ms;
          max-height: 0px;
          overflow: hidden;

          &.active {
            max-height: 200px;
            overflow: initial;
          }

          p {
            padding: var(--space-4) 0px;
            margin: 0px;
            line-height: 24px;
            font-size: var(--font-size-16);
          }
        }
      }

      .icon {
        margin-right: var(--space-4);
      }
    }
  }

  &.has-divider {
    margin-top: var(--space-4);
    padding-bottom: var(--space-10);
  }
}
</style>
